import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lock, ShieldCheck, Truck } from "lucide-react";

const emptyAddr = {
  full_name: "",
  phone: "",
  email: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
};

export default function Checkout() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, cart, clearCart } = useApp();
  const [step, setStep] = useState(1); // 1=Address, 2=Review & Pay
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [ship, setShip] = useState(emptyAddr);
  const [bill, setBill] = useState(emptyAddr);
  const [sameAsShip, setSameAsShip] = useState(true);
  const [notes, setNotes] = useState("");
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [summary, setSummary] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState("razorpay"); // "razorpay" | "cod"
  const [publicSettings, setPublicSettings] = useState(null);

  const subtotal = useMemo(
    () => cart.reduce((s, i) => s + i.price * i.qty, 0),
    [cart]
  );

  useEffect(() => {
    if (!cart.length) {
      nav("/cart");
    }
  }, [cart.length, nav]);

  // Load store settings once (to know which payment methods are enabled & the mode)
  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        setPublicSettings(data);
        const codOn = !!data.allow_cod;
        const advanceOn = data.allow_advance !== false;
        // If COD is off (default) always default to razorpay/online.
        if (!codOn) setPayMethod("razorpay");
        else if (!advanceOn && codOn) setPayMethod("cod");
      })
      .catch(() => setPublicSettings({ allow_cod: false, allow_advance: true, advance_percent: 30, payment_mode: "advance_cod" }));
  }, []);

  // Prefill from logged-in customer's addresses
  useEffect(() => {
    if (!user) return;
    api.get("/customer/addresses").then(({ data }) => {
      setSavedAddresses(data.addresses || []);
      const def = data.default_address_id
        ? (data.addresses || []).find((a) => a.id === data.default_address_id)
        : (data.addresses || [])[0];
      if (def) {
        setSelectedAddrId(def.id);
        setShip({ ...emptyAddr, ...def, email: user.email });
      } else if (user) {
        setShip((s) => ({ ...s, full_name: user.name || "", phone: user.phone || "", email: user.email || "" }));
      }
    });
  }, [user]);

  // Recalculate summary when subtotal or coupon changes
  useEffect(() => {
    if (!subtotal) return;
    const p = new URLSearchParams({ subtotal: String(subtotal) });
    if (appliedCoupon) p.set("code", appliedCoupon);
    api
      .get(`/checkout/summary?${p.toString()}`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [subtotal, appliedCoupon]);

  // Full-online mode → the "advance" is 100% of total, no COD balance.
  const isOnlineOnly = publicSettings?.payment_mode === "online_only";
  const onlineChargeAmount = isOnlineOnly ? (summary?.total ?? subtotal) : (summary?.advance_amount ?? 0);
  const onlineRemainingAmount = isOnlineOnly ? 0 : (summary?.remaining_amount ?? 0);
  const onlinePctLabel = isOnlineOnly ? 100 : (summary?.advance_percent ?? 30);

  const chooseSaved = (a) => {
    setSelectedAddrId(a.id);
    setShip({ ...emptyAddr, ...a, email: ship.email || user?.email || "" });
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.get(
        `/coupons/validate?code=${encodeURIComponent(coupon.trim())}&subtotal=${subtotal}`
      );
      if (data.valid) {
        setAppliedCoupon(coupon.trim().toUpperCase());
        toast.success(`Coupon applied — ₹${data.discount} off`);
      } else {
        toast.error(data.detail || "Invalid coupon");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const clearCoupon = () => {
    setAppliedCoupon(null);
    setCoupon("");
  };

  const validAddress = (a) =>
    a.full_name && a.phone && a.line1 && a.city && a.state && a.pincode;

  const next = () => {
    if (!validAddress(ship)) return toast.error("Please complete the shipping address");
    if (!sameAsShip && !validAddress(bill)) return toast.error("Please complete the billing address");
    setStep(2);
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const placeAndPay = async () => {
    if (placing || paying) return;
    setPlacing(true);
    try {
      // Create order in our DB (server handles COD vs razorpay branching)
      const { data: order } = await api.post("/checkout/orders", {
        items: cart.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
        shipping_address: ship,
        billing_address: sameAsShip ? null : bill,
        delivery_notes: notes,
        coupon_code: appliedCoupon,
        contact_email: ship.email || user?.email,
        contact_phone: ship.phone,
        payment_method: payMethod,
      });

      // COD → order already confirmed on the server (stock decremented + email sent). Done.
      if (payMethod === "cod") {
        // NAV FIRST — clearCart empties the cart which fires the mount guard's
        // useEffect(() => { if(!cart.length) nav('/cart') }) and would override
        // this navigation otherwise.
        nav(`/order/${order.order_no}?status=cod`);
        toast.success("Order placed — pay in cash at delivery.");
        clearCart();
        return;
      }

      // Razorpay → trigger payment
      setPaying(true);
      let intent;
      try {
        const { data } = await api.post(`/checkout/payments/create?order_id=${order.id}`);
        intent = data;
      } catch (err) {
        if (err?.response?.status === 503) {
          nav(`/order/${order.order_no}?status=pending`);
          toast.warning("Payment gateway not configured yet. Order saved — admin will contact you.");
          clearCart();
          return;
        }
        throw err;
      }

      const ok = await loadRazorpay();
      if (!ok) throw new Error("Failed to load Razorpay");

      const rzp = new window.Razorpay({
        key: intent.key_id,
        amount: intent.razorpay_amount,
        currency: intent.currency,
        name: "THE FABRICS",
        description: `Advance for order ${order.order_no}`,
        order_id: intent.razorpay_order_id,
        prefill: intent.prefill,
        theme: { color: "#000000" },
        handler: async (response) => {
          try {
            await api.post("/checkout/payments/verify", {
              order_id: order.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            nav(`/order/${order.order_no}?status=paid`);
            toast.success(isOnlineOnly ? "Payment received — thank you." : "Advance received — thank you.");
            clearCart();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
            nav(`/order/${order.order_no}?status=verify_failed`);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled. Your order is saved as pending.");
            nav(`/order/${order.order_no}?status=cancelled`);
          },
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to place order");
    } finally {
      setPlacing(false);
      setPaying(false);
    }
  };

  if (!cart.length) return null;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16" data-testid="checkout-page">
      <div className="mb-10">
        <p className="label-xs opacity-60">
          Step {step} of 2
        </p>
        <h1 className="mt-3 text-5xl font-serif tracking-tighter">Checkout</h1>
        <p className="mt-4 text-sm opacity-70">
          {isOnlineOnly
            ? <>Secure checkout — <span className="text-foreground">100% online payment</span> via Razorpay.</>
            : <>Choose <span className="text-foreground">{summary?.advance_percent ?? 30}% advance online + rest COD</span>{publicSettings?.allow_cod ? ", or pay the full amount in cash on delivery." : "."}</>}
        </p>
        {!user && (
          <p className="mt-3 text-xs opacity-60" data-testid="guest-checkout-hint">
            Checking out as guest.{" "}
            <Link to={`/login?redirect=${encodeURIComponent(location.pathname)}`} className="text-foreground link-underline" data-testid="guest-checkout-login-link">
              Log in
            </Link>{" "}
            to save your details and track future orders.
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        {/* Left: forms */}
        <div>
          {step === 1 ? (
            <div className="space-y-8" data-testid="checkout-step-address">
              {user && savedAddresses.length > 0 && (
                <section>
                  <p className="label-xs mb-3">Saved Addresses</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {savedAddresses.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => chooseSaved(a)}
                        className={`border p-4 text-left ${
                          selectedAddrId === a.id ? "border-foreground" : "border-foreground/20 hover:border-foreground"
                        }`}
                        data-testid={`saved-addr-${a.id}`}
                        style={{ transitionProperty: "border-color" }}
                      >
                        <p className="font-serif">{a.full_name}</p>
                        <p className="text-xs opacity-70 mt-1">{a.phone}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {a.line1}, {a.city}, {a.state} {a.pincode}
                        </p>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <p className="label-xs mb-4">Shipping Address</p>
                <AddressFields value={ship} onChange={setShip} prefix="ship" />
              </section>

              <section>
                <label className="flex items-center gap-3 text-sm mb-4 select-none">
                  <input
                    type="checkbox"
                    checked={sameAsShip}
                    onChange={(e) => setSameAsShip(e.target.checked)}
                    className="h-4 w-4 accent-foreground"
                    data-testid="same-as-shipping"
                  />
                  <span>Billing address is same as shipping</span>
                </label>
                {!sameAsShip && <AddressFields value={bill} onChange={setBill} prefix="bill" />}
              </section>

              <section>
                <Label className="label-xs">Delivery Notes (optional)</Label>
                <Textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="rounded-none mt-2"
                  placeholder="Special instructions for the courier or brand team…"
                  data-testid="delivery-notes"
                />
              </section>

              <div className="flex justify-end gap-3">
                <Link to="/cart">
                  <Button variant="outline" className="rounded-none border-foreground label-xs h-12 px-6">
                    Back to Cart
                  </Button>
                </Link>
                <Button
                  onClick={next}
                  className="rounded-none label-xs h-12 px-8"
                  data-testid="checkout-continue"
                >
                  Continue to Payment →
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-8" data-testid="checkout-step-review">
              <section>
                <p className="label-xs mb-3">Shipping to</p>
                <div className="border p-4">
                  <p className="font-serif">{ship.full_name}</p>
                  <p className="text-sm opacity-70 mt-1">{ship.phone}{ship.email ? ` · ${ship.email}` : ""}</p>
                  <p className="text-sm opacity-70 mt-1">
                    {ship.line1}{ship.line2 ? `, ${ship.line2}` : ""}, {ship.city}, {ship.state} {ship.pincode}
                  </p>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="mt-2 text-xs link-underline"
                  data-testid="edit-address-btn"
                >
                  Edit
                </button>
              </section>

              <section>
                <p className="label-xs mb-3">Coupon</p>
                {appliedCoupon ? (
                  <div className="border p-4 flex items-center justify-between" data-testid="applied-coupon">
                    <div>
                      <p className="font-serif">{appliedCoupon}</p>
                      <p className="text-xs opacity-70">Applied to your order</p>
                    </div>
                    <button className="label-xs link-underline" onClick={clearCoupon} data-testid="remove-coupon">
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter code"
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                      className="rounded-none uppercase"
                      data-testid="coupon-input"
                    />
                    <Button
                      type="button"
                      onClick={applyCoupon}
                      variant="outline"
                      className="rounded-none border-foreground label-xs"
                      data-testid="apply-coupon"
                    >
                      Apply
                    </Button>
                  </div>
                )}
              </section>

              <section>
                <p className="label-xs mb-3">Items ({cart.length})</p>
                <div className="border divide-y">
                  {cart.map((i) => (
                    <div key={`${i.product_id}-${i.size}`} className="p-4 flex gap-3 items-center">
                      {i.image && <img src={i.image} alt="" className="w-14 h-16 object-cover" />}
                      <div className="flex-1">
                        <p className="text-sm">{i.name}</p>
                        <p className="text-xs opacity-60">Size {i.size} · Qty {i.qty}</p>
                      </div>
                      <p className="text-sm">{money(i.price * i.qty)}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Payment method */}
              <section data-testid="payment-methods">
                <p className="label-xs mb-3">Payment Method</p>
                <div className="space-y-3">
                  {(publicSettings?.allow_advance ?? true) && (
                    <label
                      className={`flex gap-4 border p-4 cursor-pointer ${
                        payMethod === "razorpay" ? "border-foreground" : "border-foreground/20 hover:border-foreground/60"
                      }`}
                      style={{ transitionProperty: "border-color" }}
                      data-testid="pm-razorpay"
                    >
                      <input
                        type="radio"
                        name="pm"
                        checked={payMethod === "razorpay"}
                        onChange={() => setPayMethod("razorpay")}
                        className="mt-1 h-4 w-4 accent-foreground"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <p className="font-serif">
                            {isOnlineOnly ? "Pay Online (Razorpay)" : `Pay ${onlinePctLabel}% Advance Online`}
                          </p>
                          <p className="text-sm font-body">{money(onlineChargeAmount)}</p>
                        </div>
                        <p className="text-xs opacity-70 mt-1">
                          {isOnlineOnly
                            ? "Secure Razorpay payment · Full amount charged now"
                            : `Secure Razorpay payment · Remaining ${money(onlineRemainingAmount)} on delivery (cash)`}
                        </p>
                      </div>
                    </label>
                  )}
                  {(publicSettings?.allow_cod === true) && (
                    <label
                      className={`flex gap-4 border p-4 cursor-pointer ${
                        payMethod === "cod" ? "border-foreground" : "border-foreground/20 hover:border-foreground/60"
                      }`}
                      style={{ transitionProperty: "border-color" }}
                      data-testid="pm-cod"
                    >
                      <input
                        type="radio"
                        name="pm"
                        checked={payMethod === "cod"}
                        onChange={() => setPayMethod("cod")}
                        className="mt-1 h-4 w-4 accent-foreground"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <p className="font-serif">Cash on Delivery</p>
                          <p className="text-sm font-body">{money(summary?.total ?? 0)}</p>
                        </div>
                        <p className="text-xs opacity-70 mt-1">
                          Pay the full amount in cash when your order arrives
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              </section>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="rounded-none border-foreground label-xs h-12 px-6"
                >
                  ← Back
                </Button>
                <Button
                  onClick={placeAndPay}
                  disabled={placing || paying}
                  className="rounded-none label-xs h-12 px-8 gap-2"
                  data-testid="pay-btn"
                >
                  <Lock className="h-3.5 w-3.5" />
                  {placing
                    ? "Preparing…"
                    : paying
                    ? "Opening Payment…"
                    : payMethod === "cod"
                    ? `Place COD Order · ${money(summary?.total ?? 0)}`
                    : isOnlineOnly
                    ? `Pay ${money(onlineChargeAmount)} Online`
                    : `Pay ${money(onlineChargeAmount)} Advance`}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Right: summary */}
        <aside className="border p-6 h-fit lg:sticky lg:top-28" data-testid="checkout-summary">
          <p className="label-xs">Order Summary</p>
          <dl className="mt-5 space-y-3 text-sm">
            <Row label="Subtotal" value={money(summary?.subtotal ?? subtotal)} />
            {summary?.discount ? <Row label={`Discount (${appliedCoupon})`} value={`− ${money(summary.discount)}`} /> : null}
            <Row
              label={summary?.shipping ? "Shipping" : "Shipping"}
              value={summary?.shipping ? money(summary.shipping) : <span className="font-serif">Free</span>}
            />
            {summary && summary.free_shipping_threshold > 0 && !summary.shipping ? (
              <p className="text-[11px] opacity-60">Free shipping unlocked (≥ ₹{summary.free_shipping_threshold})</p>
            ) : summary && summary.free_shipping_threshold ? (
              <p className="text-[11px] opacity-60">
                Add {money(summary.free_shipping_threshold - (summary.subtotal - summary.discount))} for free shipping
              </p>
            ) : null}
          </dl>
          <div className="border-t mt-4 pt-4 flex justify-between items-baseline">
            <span className="label-xs">Total</span>
            <span className="text-2xl font-body" data-testid="summary-total">{money(summary?.total ?? subtotal)}</span>
          </div>
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between">
              <span>{isOnlineOnly ? "Pay online now" : `Advance now (${onlinePctLabel}%)`}</span>
              <span className="font-body" data-testid="summary-advance">{money(onlineChargeAmount)}</span>
            </div>
            {!isOnlineOnly && (
              <div className="flex justify-between opacity-70">
                <span>Remaining later</span>
                <span data-testid="summary-remaining">{money(onlineRemainingAmount)}</span>
              </div>
            )}
          </div>
          <div className="mt-6 space-y-3 text-xs opacity-70">
            <p className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Secure payments via Razorpay</p>
            <p className="flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> Ships across India</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function AddressFields({ value, onChange, prefix }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-xs">Full name</Label>
          <Input required value={value.full_name} onChange={(e) => set("full_name", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-full-name`} />
        </div>
        <div>
          <Label className="label-xs">Phone</Label>
          <Input required value={value.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-phone`} />
        </div>
      </div>
      <div>
        <Label className="label-xs">Email {prefix === "ship" ? "(for confirmation)" : "(optional)"}</Label>
        <Input type="email" value={value.email || ""} onChange={(e) => set("email", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-email`} />
      </div>
      <div>
        <Label className="label-xs">Address line 1</Label>
        <Input required value={value.line1} onChange={(e) => set("line1", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-line1`} />
      </div>
      <div>
        <Label className="label-xs">Address line 2 (optional)</Label>
        <Input value={value.line2 || ""} onChange={(e) => set("line2", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-line2`} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="label-xs">City</Label>
          <Input required value={value.city} onChange={(e) => set("city", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-city`} />
        </div>
        <div>
          <Label className="label-xs">State</Label>
          <Input required value={value.state} onChange={(e) => set("state", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-state`} />
        </div>
        <div>
          <Label className="label-xs">Pincode</Label>
          <Input required value={value.pincode} onChange={(e) => set("pincode", e.target.value)} className="rounded-none mt-2" data-testid={`${prefix}-pincode`} />
        </div>
      </div>
    </div>
  );
}
